import { useQuery } from '@tanstack/react-query'
import {
  Button,
  DatePicker,
  Flex,
  Select,
  Tag,
  Tooltip,
  Typography
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import type { TFunction } from 'i18next'
import { RotateCw } from 'lucide-react'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import {
  SeverityEnum,
  organizationsAuditLogsListOptions,
  type AuditLogList,
  type OrganizationsAuditLogsListData
} from 'src/api/main'
import {
  ActiveFilterTags,
  BadgeText,
  AuditActionTag,
  DateTimeCell,
  FilterSelect,
  MoreFilters,
  ProTableContainer,
  QueryErrorAlert,
  SeverityDot
} from 'src/components'
import { FILTER_CONTROL_WIDTH } from 'src/constants'
import { FilterLabelsProvider } from 'src/context'
import { useFacetFilter, useQueryParams, useQueryUpdate } from 'src/hooks'
import {
  SEVERITY_COLORS,
  SEVERITY_TAG_COLOR,
  defaultFilters,
  getAuditEventCode,
  humanizeAuditCode,
  normalizeAuditSeverity,
  toAuditAcceptLanguage,
  translateAuditAction,
  translateAuditSeverity
} from 'src/utils'
import type { ActiveFilterConfig } from 'src/utils'

const { Text } = Typography
const { RangePicker } = DatePicker

type Props = {
  workspaceId: number
  userId: number
}

type SeverityFilter = SeverityEnum | 'all'

type AuditListQuery = NonNullable<OrganizationsAuditLogsListData['query']>

type DecoratedEvent = AuditLogList & {
  severityValue: SeverityEnum | null
  eventTypeLabel: string | null
  descriptionLabel: string
}

const decorateEvent = (event: AuditLogList, t: TFunction): DecoratedEvent => ({
  ...event,
  severityValue: normalizeAuditSeverity(event.severity),
  eventTypeLabel: event.event_type
    ? translateAuditAction(t, event.event_type)
    : null,
  descriptionLabel:
    event.description?.trim() || humanizeAuditCode(getAuditEventCode(event))
})

const UserAuditTabBase = ({ workspaceId, userId }: Props) => {
  const { t, i18n } = useTranslation()
  const language = i18n.resolvedLanguage ?? i18n.language
  const acceptLanguage = toAuditAcceptLanguage(language)

  const params = useQueryParams()
  const { updateQuery } = useQueryUpdate()

  const page = Number(params.page) || defaultFilters.page
  const perPage =
    Number(params.page_size ?? params.per_page) || defaultFilters.per_page
  const searchTerm = String(params.search ?? '')
  const serviceFilter = String(params.service ?? '')
  const eventTypeFilter = String(params.event_type ?? '')
  const severityFilter =
    (params.severity as SeverityFilter | undefined) ?? 'all'
  const fromFilter = String(params.from ?? '')
  const toFilter = String(params.to ?? '')

  const enabled = workspaceId > 0 && userId > 0

  const auditQuery = useMemo<AuditListQuery>(
    () => ({
      actor_id: String(userId),
      limit: perPage,
      offset: (page - 1) * perPage,
      search: searchTerm || undefined,
      service: serviceFilter || undefined,
      event_type: eventTypeFilter || undefined,
      severity: severityFilter === 'all' ? undefined : severityFilter,
      timestamp_gte: fromFilter || undefined,
      timestamp_lte: toFilter || undefined
    }),
    [
      userId,
      perPage,
      page,
      searchTerm,
      serviceFilter,
      eventTypeFilter,
      severityFilter,
      fromFilter,
      toFilter
    ]
  )

  const { data, isLoading, isFetching, isError, refetch } = useQuery({
    ...organizationsAuditLogsListOptions({
      path: { organization_id: workspaceId },
      query: auditQuery,
      headers: { 'Accept-Language': acceptLanguage }
    }),
    enabled,
    placeholderData: (previousData) => previousData,
    // Opt out of the app-wide refetchOnMount:false. New events land constantly, so
    // re-entering the page must hit the backend instead of rendering the old page.
    refetchOnMount: 'always',
    // Audit rows are append-only and land constantly: also re-fetch when the user
    // comes back to the browser tab, since a mount-only refetch misses the case
    // where the action was performed elsewhere and this page stayed open.
    refetchOnWindowFocus: true
  })

  const rows = useMemo(
    () => (data?.results ?? []).map((event) => decorateEvent(event, t)),
    [data?.results, t]
  )
  const total = data?.count ?? 0

  // Options come from the org-wide `/filters` facet, not this user's recent
  // events: sampling the last N events would drop any service/type the user
  // only used outside that window (e.g. 100 actions in one app today hides
  // yesterday's other apps). The facet lists the complete set — a value with no
  // rows for this user is harmless, missing one is not.
  const service = useFacetFilter({
    organizationId: workspaceId,
    field: 'service',
    selected: serviceFilter,
    getLabel: humanizeAuditCode,
    enabled
  })
  const eventLabel = useCallback(
    (value: string) => translateAuditAction(t, value),
    [t]
  )
  const eventType = useFacetFilter({
    organizationId: workspaceId,
    field: 'event_type',
    selected: eventTypeFilter,
    getLabel: eventLabel,
    enabled
  })

  const rangeValue = useMemo<[Dayjs | null, Dayjs | null] | null>(() => {
    const fromDay = fromFilter ? dayjs(fromFilter) : null
    const toDay = toFilter ? dayjs(toFilter) : null

    return fromDay || toDay ? [fromDay, toDay] : null
  }, [fromFilter, toFilter])

  const setFilter = (patch: Record<string, unknown>) =>
    updateQuery({ ...params, page: defaultFilters.page, ...patch })

  const filterTagItems: ActiveFilterConfig[] = [
    {
      type: 'select',
      key: 'service',
      label: t('users.detail.audit.filters.allApplications')
    },
    {
      type: 'select',
      key: 'event_type',
      label: t('users.detail.audit.filters.allEventTypes')
    },
    {
      type: 'select',
      key: 'severity',
      label: t('users.detail.audit.filters.allSeverities')
    },
    {
      type: 'dateRange',
      key: 'from',
      from: 'from',
      to: 'to',
      label: t('users.detail.audit.dateRangeLabel'),
      clearKeys: ['from', 'to']
    }
  ]

  const moreFiltersActiveCount = [
    severityFilter === 'all' ? '' : severityFilter,
    fromFilter || toFilter
  ].filter((value) => value !== '').length

  const resetMoreFilters = () =>
    setFilter({
      severity: null,
      from: null,
      to: null
    })

  const clearFilters = () => {
    updateQuery({}, true)
  }

  const handleRangeChange = (next: [Dayjs | null, Dayjs | null] | null) => {
    setFilter({
      from: next?.[0] ? next[0].startOf('day').toISOString() : null,
      to: next?.[1] ? next[1].endOf('day').toISOString() : null
    })
  }

  const columns: ColumnsType<DecoratedEvent> = useMemo(
    () => [
      {
        title: '',
        key: 'severity_dot',
        fixed: 'left',
        width: 24,
        align: 'center',
        render: (_v, record) => <SeverityDot severity={record.severityValue} />
      },
      {
        title: t('users.detail.audit.columns.time'),
        dataIndex: 'timestamp',
        key: 'timestamp',
        fixed: 'left',
        width: 150,
        render: (value: string) => (
          <DateTimeCell
            value={value}
            variant="relative"
            style={{ fontSize: 12 }}
          />
        )
      },
      {
        title: t('users.detail.audit.columns.event'),
        key: 'event',
        width: 180,
        render: (_v, record) =>
          record.eventTypeLabel ? (
            <Tag style={{ marginInlineEnd: 0, fontSize: 11 }}>
              {record.eventTypeLabel}
            </Tag>
          ) : (
            <AuditActionTag action={getAuditEventCode(record)} maxWidth={165} />
          )
      },
      {
        title: t('users.detail.audit.columns.application'),
        dataIndex: 'application_name',
        key: 'application',
        width: 160,
        render: (name: string | null, record) => {
          const label = name || humanizeAuditCode(record.service)

          return label ? (
            <Text style={{ fontSize: 12 }}>{label}</Text>
          ) : (
            <Text type="secondary">—</Text>
          )
        }
      },
      {
        title: t('users.detail.audit.columns.description'),
        dataIndex: 'descriptionLabel',
        key: 'description',
        ellipsis: { showTitle: false },
        render: (description: string) =>
          description ? (
            <Tooltip placement="topLeft" title={description}>
              <Text style={{ fontSize: 12 }}>{description}</Text>
            </Tooltip>
          ) : (
            <Text type="secondary">—</Text>
          )
      },
      {
        title: t('users.detail.audit.columns.severity'),
        dataIndex: 'severityValue',
        key: 'severity',
        width: 110,
        render: (severity: SeverityEnum | null) =>
          severity ? (
            <Tag
              color={SEVERITY_TAG_COLOR[severity]}
              style={{
                marginInlineEnd: 0,
                fontSize: 10,
                textTransform: 'capitalize'
              }}
            >
              {translateAuditSeverity(t, severity)}
            </Tag>
          ) : (
            <Text type="secondary">—</Text>
          )
      },
      {
        title: t('users.detail.audit.columns.ip'),
        dataIndex: 'client_ip',
        key: 'ip',
        width: 130,
        render: (ip: string | null) =>
          ip ? (
            <Text code style={{ fontSize: 11 }}>
              {ip}
            </Text>
          ) : (
            <Text type="secondary">—</Text>
          )
      }
    ],
    [t]
  )

  const filtersDirty =
    searchTerm !== '' ||
    severityFilter !== 'all' ||
    serviceFilter !== '' ||
    eventTypeFilter !== '' ||
    fromFilter !== '' ||
    toFilter !== ''

  if (isError) {
    return <QueryErrorAlert onRetry={() => void refetch()} />
  }

  return (
    <ProTableContainer<DecoratedEvent>
      tableIdentifier="user-audit"
      searchPlaceholder={t('users.detail.audit.searchPlaceholder', {
        defaultValue: 'Search events'
      })}
      rowKey="id"
      columns={columns}
      data={{ count: total, results: rows }}
      loading={{ spinning: isLoading || isFetching, delay: 200 }}
      showSorterTooltip={false}
      scroll={{ x: 1010 }}
      locale={{
        emptyText: filtersDirty
          ? t('users.detail.audit.noMatches')
          : t('users.detail.audit.empty')
      }}
      leftChildren={
        <>
          <FilterSelect
            value={serviceFilter}
            options={service.options}
            onSelect={(value) => setFilter({ service: value || null })}
            filterKey="service"
            onSearch={service.onSearch}
            placeholder={t('users.detail.audit.filters.allApplications')}
            width={FILTER_CONTROL_WIDTH}
          />
          <FilterSelect
            value={eventTypeFilter}
            options={eventType.options}
            onSelect={(value) => setFilter({ event_type: value || null })}
            filterKey="event_type"
            onSearch={eventType.onSearch}
            placeholder={t('users.detail.audit.filters.allEventTypes')}
            width={FILTER_CONTROL_WIDTH}
          />
          <MoreFilters
            filterActiveCount={moreFiltersActiveCount}
            onResetMoreFilters={resetMoreFilters}
            buttonProps={{ size: 'small' }}
          >
            <MoreFilters.Col
              title={t('users.detail.audit.filters.allSeverities')}
            >
              <Select<SeverityFilter>
                size="small"
                allowClear
                placeholder={t('users.detail.audit.filters.allSeverities')}
                value={severityFilter === 'all' ? undefined : severityFilter}
                onChange={(value) => setFilter({ severity: value ?? null })}
                style={{ width: '100%' }}
                options={[
                  {
                    value: SeverityEnum.LOW,
                    label: (
                      <BadgeText
                        text={translateAuditSeverity(t, SeverityEnum.LOW)}
                        color={SEVERITY_COLORS[SeverityEnum.LOW]}
                      />
                    )
                  },
                  {
                    value: SeverityEnum.MEDIUM,
                    label: (
                      <BadgeText
                        text={translateAuditSeverity(t, SeverityEnum.MEDIUM)}
                        color={SEVERITY_COLORS[SeverityEnum.MEDIUM]}
                      />
                    )
                  },
                  {
                    value: SeverityEnum.HIGH,
                    label: (
                      <BadgeText
                        text={translateAuditSeverity(t, SeverityEnum.HIGH)}
                        color={SEVERITY_COLORS[SeverityEnum.HIGH]}
                      />
                    )
                  }
                ]}
              />
            </MoreFilters.Col>

            <MoreFilters.Col title={t('users.detail.audit.dateRangeLabel')}>
              <RangePicker
                size="small"
                style={{ width: '100%' }}
                value={rangeValue}
                onChange={(next) =>
                  handleRangeChange(next as [Dayjs | null, Dayjs | null] | null)
                }
                aria-label={t('users.detail.audit.dateRangeLabel')}
              />
            </MoreFilters.Col>
          </MoreFilters>

          <Flex flex="1 0 100%">
            <ActiveFilterTags
              items={filterTagItems}
              onClearAll={filtersDirty ? clearFilters : undefined}
            />
          </Flex>
        </>
      }
      rightChildren={
        <Flex align="center" gap={8}>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {t('users.detail.audit.toolbar.events', { count: total })}
          </Text>
          <Tooltip title={t('users.detail.audit.toolbar.refresh')}>
            <Button
              size="small"
              type="text"
              icon={<RotateCw size={14} />}
              onClick={() => void refetch()}
              loading={isFetching}
              aria-label={t('users.detail.audit.toolbar.refresh')}
            />
          </Tooltip>
        </Flex>
      }
    />
  )
}

export const UserAuditTab = (props: Props) => (
  <FilterLabelsProvider>
    <UserAuditTabBase {...props} />
  </FilterLabelsProvider>
)
